export default class StringUtils {
  private static readonly nameRegex = /^[a-zA-ZÀ-ÖØ-öø-ÿ]+(?:['-][a-zA-ZÀ-ÖØ-öø-ÿ]+)*(?:\s+[a-zA-ZÀ-ÖØ-öø-ÿ]+(?:['-][a-zA-ZÀ-ÖØ-öø-ÿ]+)*)+$/;

  static isValidEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  static formatCEP(valor: string): string {
    if (typeof valor !== 'string') {
      return valor;
    }

    const valorComZeros = valor.padStart(8, '0');
    const cepFormatado = valorComZeros.replace(/(\d{5})(\d{3})/, '$1-$2');

    return cepFormatado;
  }

  static formatCNPJSemMatrizEDigito(cnpj: string): string {
    const cnpjPattern = /^([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})(\d{2})$/;

    if (cnpjPattern.test(cnpj.toUpperCase())) {
      return cnpj.toUpperCase().replace(cnpjPattern, '$1.$2.$3/XXXX-XX');
    } else {
      return cnpj;
    }
  }

  static formatCPF(value: string): string {
    const numeric = value.replace(/\D/g, '').slice(0, 11);
    return numeric
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }

  static formatTelefone(value: string): string {
    const numeric = (value ?? '').replace(/\D/g, '').slice(0, 11);
    if (numeric.length <= 2) {
      return numeric;
    }

    const ddd = numeric.slice(0, 2);
    const rest = numeric.slice(2);
    if (rest.length <= 4) {
      return `(${ddd}) ${rest}`;
    }

    const splitAt = numeric.length > 10 ? 5 : 4;
    return `(${ddd}) ${rest.slice(0, splitAt)}-${rest.slice(splitAt)}`;
  }

  static formatCNPJ(value: string): string {
    const alphanumeric = this.sanitizeCNPJ(value);
    return alphanumeric
      .replace(/^([A-Z0-9]{2})([A-Z0-9])/, '$1.$2')
      .replace(/^([A-Z0-9]{2})\.([A-Z0-9]{3})([A-Z0-9])/, '$1.$2.$3')
      .replace(/\.([A-Z0-9]{3})([A-Z0-9])/, '.$1/$2')
      .replace(/([A-Z0-9]{4})(\d)/, '$1-$2');
  }

  static formatCPForCNPJ(value: string): string {
    const normalized = this.normalizeCNPJ(value);

    if (normalized.length === 11) {
      return this.formatCPF(normalized);
    }

    if (normalized.length === 14) {
      return this.formatCNPJ(normalized);
    }

    return value;
  }

  static normalizeCNPJ(value: string): string {
    return String(value ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  static sanitizeCNPJ(value: string): string {
    return this.normalizeCNPJ(value).slice(0, 14);
  }

  static isEmpty(value: any): boolean {
    if (value == null) return true;
    if (typeof value === 'string' || Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
  }

  static getBooleanValue(value: any): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value !== 'string') return false;

    const normalizedValue = value.trim().toLowerCase();
    return normalizedValue === 's' || normalizedValue === 'true' || normalizedValue === '1';
  }

  static format(str: string, ...args: any[]) {
    let i = 0;
    return str.replace(/%s/g, () => args[i++]);
  }

  static validateCPForCNPJ(valor: string): boolean {
    valor = this.normalizeCNPJ(valor);

    if (valor.length === 11) {
      return this.validateCPF(valor);
    }

    if (valor.length === 14) {
      return this.validateCNPJ(valor);
    }

    return false;
  }

  static validateCPF(valor: string): boolean {
    let soma: number = 0;
    let resto: number;

    // Verifica se todos os números são iguais
    if (/^(\d)\1+$/.test(valor)) {
      return false;
    }

    // Validação do primeiro dígito verificador do CPF
    for (let i = 1; i <= 9; i++) {
      soma += parseInt(valor.charAt(i - 1)) * (11 - i);
    }
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(valor.charAt(9))) {
      return false;
    }

    // Validação do segundo dígito verificador do CPF
    soma = 0;
    for (let i = 1; i <= 10; i++) {
      soma += parseInt(valor.charAt(i - 1)) * (12 - i);
    }
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(valor.charAt(10))) {
      return false;
    }

    return true;
  }

  private static charToCnpjNumericValue(c: string): number {
    return c.charCodeAt(0) - 48;
  }

  static validateCNPJ(valor: string): boolean {
    valor = valor.toUpperCase();

    if (valor.length !== 14) {
      return false;
    }

    if (/^(.)\1+$/.test(valor)) {
      return false;
    }

    let tamanho: number = 12;
    let numeros: string = valor.substring(0, tamanho);
    const digitos: string = valor.substring(tamanho);
    let soma: number = 0;
    let pos: number = tamanho - 7;

    for (let i = tamanho; i >= 1; i--) {
      soma += this.charToCnpjNumericValue(numeros.charAt(tamanho - i)) * pos--;
      if (pos < 2) pos = 9;
    }

    let resultado: number = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== parseInt(digitos.charAt(0))) {
      return false;
    }

    tamanho = 13;
    numeros = valor.substring(0, tamanho);
    soma = 0;
    pos = tamanho - 7;

    for (let i = tamanho; i >= 1; i--) {
      soma += this.charToCnpjNumericValue(numeros.charAt(tamanho - i)) * pos--;
      if (pos < 2) pos = 9;
    }

    resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    return resultado === parseInt(digitos.charAt(1));
  }

  /**
   * Valida a quantidade de caracteres de uma string.
   *
   * @param str - A string a ser validada.
   * @param maxLength - A quantidade máxima de caracteres permitida.
   * @returns true se a string atender o limite, false caso contrário.
   */
  static validateStringLength(str: string, maxLength: number): boolean {
    return String(str).length <= maxLength;
  }

  /**
   * Retorna caracteres atribuindo um limite maximo para exibição.
   *
   * @param str - A string a ser validada.
   * @param maxLength - A quantidade máxima de caracteres permitida.
   * @returns retorna o valor válido pelo máximo de caracter removendo os demais campos extras.
   */
  static truncateToMaxLength(str: string, maxLength: number) {
    const strInput = String(str);
    return strInput.slice(0, maxLength);
  }

  static validarCEP(cep: string): boolean {
    const cepSemHifen = cep.replace('-', '');

    const cepValido = /^\d{8}$/.test(cepSemHifen);

    return cepValido;
  }

  static formatNumber(numeroStr: string | number) {
    if (numeroStr === null || numeroStr === undefined) return '';
    const str = String(numeroStr);

    const apenasDigitos = str.replace(/\D/g, '');
    if (!apenasDigitos) return '';

    return Number(apenasDigitos).toLocaleString('pt-BR');
  }

  public static camelCaseToSnakeCase(str: string): string {
    return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
  }

  public static validateName(str: string) {
    return str && this.nameRegex.test(str.trim()) && (str.trim()).split(' ').length >= 2;
  }
  
  static formatBRL(value: number, fractionDigits: number = 2): string {
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits })}`;
  }

  static formatPercent(value: number, fractionDigits: number = 0): string {
    return `${value.toFixed(fractionDigits)}%`;
  }

  static safeParseInt(value: string | undefined | null, fallback: number = 0): number {
    if (value === undefined || value === null) {
      return fallback;
    }
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  static getNormalizedValue(value: string) {
    return (value || '').toString().toLowerCase();
  }
}
